import { useEffect, useRef } from 'react';
import { subscribeCacheUpdated } from '@/lib/utils/indexedDbCache';

/** Joins the prefix list into a stable dependency key; cache prefixes never contain it. */
const PREFIX_KEY_SEPARATOR = '|';

/**
 * Re-run `onUpdate` whenever a cache under one of `prefixes` is rewritten (see
 * `subscribeCacheUpdated`). For components that render from a cache they don't own —
 * e.g. the campaign sidebar reading the campaign list while a stale-while-revalidate
 * read elsewhere refreshes it — so they pick up the fresh data with a cheap cache-hit
 * re-read instead of showing the stale value until the next mount.
 *
 * `onUpdate` is read through a ref, so passing an inline closure is fine.
 */
export function useCacheUpdated(
  prefixes: string | readonly string[],
  onUpdate: (prefix: string) => void,
): void {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // Stable dependency for an array prop.
  const key = typeof prefixes === 'string' ? prefixes : prefixes.join(PREFIX_KEY_SEPARATOR);

  useEffect(() => {
    const wanted = key.split(PREFIX_KEY_SEPARATOR);
    return subscribeCacheUpdated((prefix) => {
      if (wanted.includes(prefix)) onUpdateRef.current(prefix);
    });
  }, [key]);
}
